import { ScreenerFilter, WatchList } from '@api';
import { Icon } from '@design';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import filterStyles from '@terminal/components/filter/Filter.module.scss';
import { FilterLabel } from '@terminal/components/filter/FilterLabel';
import { AssetsOptionsList } from '@terminal/components/filters/components/filter-assets/AssetsOptionsList';
import { FilterResetButton } from '@terminal/components/filters/components/filter-reset-button/FilterResetButton';
import {
  FilterOption,
  ToggleFilterProps,
} from '@terminal/components/update-filter-options/updateFilterOptions';
import {
  INDEX_ID,
  SCREENER_ID,
  WATCHLIST_ID,
  WORKING_LIST_ID,
} from '@terminal/config/constants';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useWorkingList } from '@terminal/hooks/use-working-list/useWorkingList';
import { appPaths } from '@terminal/routes/app-paths';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { FilterSearch } from '@terminal/views/screener/filter-search/FilterSearch';
import {
  ASSET_FILTERS_KEY,
  isFilterWithChildOptionActive,
} from '@terminal/views/screener/use-screener-store/filter-utils';
import { useScreenerStore } from '@terminal/views/screener/use-screener-store/useScreenerStore';
import { widgetPaths } from '@terminal/views/widgets/widget-utils';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  AssetFilterValueKey,
  AssetsFilterOption,
  isAssetFilterValueKey,
  mapActiveFilterOptions,
  mapToFilterOptions,
} from '../utils';
import styles from './FilterAssets.module.scss';

interface FilterAssetsProps {
  filter: ScreenerFilter;
  filterOptions: FilterOption[];
  publicLists: WatchList[];
  withScreeners?: boolean;
  withWatchlists?: boolean;
  withWorkingLists?: boolean;
  addFilterOption: (props: ToggleFilterProps) => void;
  removeFilterOption?: (props: ToggleFilterProps) => void;
  resetFilterOptions?: (filterKey: string) => void;
  enableSearchInFilterAssets?: boolean;
  showListTitle?: boolean;
  multiselect?: boolean;
  dropdownMaxHeight?: string;
  isPending?: boolean;
  source: PosthogTrackingSource;
}

export const FilterAssets = ({
  filter,
  filterOptions,
  publicLists,
  withScreeners = true,
  withWatchlists = true,
  withWorkingLists = true,
  addFilterOption,
  removeFilterOption,
  resetFilterOptions,
  enableSearchInFilterAssets = true,
  showListTitle = true,
  multiselect = false,
  dropdownMaxHeight,
  isPending,
  source,
}: FilterAssetsProps) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const { trackScreenerOpen } = usePosthogEvents();
  const watchLists = useWatchlist(store => store.lists);
  const savedScreens = useScreenerStore(state => state.savedScreens);
  const hasWorkingList =
    useWorkingList(store => !!store.workingList.length) && withWorkingLists;
  const { setQueryParams } = useQueryParams();

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleClear = () => {
    setSearchValue('');
  };

  const handleReset = (e: Event) => {
    e.preventDefault();
    resetFilterOptions?.(filter.key);
    setSearchValue('');
  };

  const isChecked = (optKey: string) =>
    mappedActiveOptions.some(f => f.key === optKey);

  const onFilterSelect = (props: ToggleFilterProps, active: boolean) => {
    if (active) {
      removeFilterOption?.(props);
    } else {
      addFilterOption(props);
    }
  };

  const valueKeyToCustomHeaderTitleMap: Record<
    AssetFilterValueKey,
    string | undefined
  > = {
    [WORKING_LIST_ID]: undefined,
    [WATCHLIST_ID]: t('common:filters.watchlist'),
    [SCREENER_ID]: t('common:filters.savedScreen'),
    [INDEX_ID]: t('common:filters.index'),
  };

  const workingListOption: AssetsFilterOption[] = hasWorkingList
    ? [
        {
          name: t('common:filters.workingList'),
          key: WORKING_LIST_ID,
          value: { workingList: 'working-list' },
          hasBottomBorder: true,
        },
      ]
    : [];

  const watchlistOptions = useMemo(
    () =>
      withWatchlists
        ? mapToFilterOptions({
            items: watchLists,
            valueKey: WATCHLIST_ID,
            customHeaderTitle: valueKeyToCustomHeaderTitleMap[WATCHLIST_ID],
            hasBottomBorder: true,
            withDefaultOption: true,
            customOptionLabel: t('common:filters.createWatchlist'),
            customOptionCallback: () =>
              setQueryParams({ widget: widgetPaths.watchlistManagerModal }),
          })
        : [],
    [watchLists, withWatchlists]
  );

  const screenOptions = useMemo(
    () =>
      withScreeners
        ? mapToFilterOptions({
            items: savedScreens,
            valueKey: SCREENER_ID,
            customHeaderTitle: valueKeyToCustomHeaderTitleMap[SCREENER_ID],
            hasBottomBorder: true,
            withDefaultOption: true,
            customOptionLabel: t('common:filters.createScreen'),
            customOptionCallback: () => {
              trackScreenerOpen(source);
              navigate(appPaths.screener);
            },
          })
        : [],
    [savedScreens, withScreeners, source]
  );
  const optionGroups = [
    workingListOption,
    watchlistOptions,
    screenOptions,
    mapToFilterOptions({
      items: publicLists,
      valueKey: INDEX_ID,
      customHeaderTitle: valueKeyToCustomHeaderTitleMap[INDEX_ID],
      hasBottomBorder: true,
    }),
  ];

  const activeWorkingListOption = filterOptions.find(
    f => f.key === WORKING_LIST_ID
  );
  const mappedActiveOptions = [
    ...(activeWorkingListOption ? [activeWorkingListOption] : []),
    ...mapActiveFilterOptions(watchLists, WATCHLIST_ID, filterOptions),
    ...mapActiveFilterOptions(savedScreens, SCREENER_ID, filterOptions),
    ...mapActiveFilterOptions(publicLists, INDEX_ID, filterOptions),
  ];
  const isParentOptionActive = isFilterWithChildOptionActive(filterOptions);

  const filteredOptions = optionGroups.flatMap(optionGroup => {
    const filteredGroup = optionGroup.filter(option =>
      option.name.toLowerCase().includes(searchValue.toLowerCase())
    );
    return filteredGroup.map((option, idx) => {
      const optionValueKey = Object.keys(option.value)[0];
      const parsedOption = { ...option };

      if (idx === 0) {
        parsedOption.customHeaderTitle =
          showListTitle && isAssetFilterValueKey(optionValueKey)
            ? valueKeyToCustomHeaderTitleMap[optionValueKey]
            : undefined;
      }

      if (idx === filteredGroup.length - 1) {
        parsedOption.hasBottomBorder = true;
      }

      return parsedOption;
    });
  });

  const noResultsFound = !filteredOptions.length;

  const triggerLabel =
    (filter.key === ASSET_FILTERS_KEY && filter.name) ||
    mappedActiveOptions.length
      ? filter.name
      : t('common:filters.selectAssetList');

  return (
    <RadixDropdownMenu.Root
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          handleClear();
        }

        setOpen(isOpen);
      }}
    >
      <RadixDropdownMenu.Trigger
        className={
          isParentOptionActive
            ? filterStyles.triggerActive
            : filterStyles.trigger
        }
        data-filter={filter.key}
        data-testid="assets-filter"
      >
        <span className={filterStyles.triggerLabel} data-trigger-label>
          {triggerLabel}
        </span>
        <FilterLabel options={mappedActiveOptions} multiselect={multiselect} />
        <Icon
          iconName="ChevronLightDown"
          size={14}
          fillColor="var(--icon-soft)"
        />
      </RadixDropdownMenu.Trigger>
      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          className={clsx(
            filterStyles.dropdownMenuContent,
            dropdownMaxHeight && styles.styledFilterDropdownMenuContent
          )}
          align="start"
          sideOffset={5}
          collisionPadding={5}
          style={
            dropdownMaxHeight
              ? ({
                  '--filter-max-height': dropdownMaxHeight,
                } as React.CSSProperties)
              : undefined
          }
        >
          {enableSearchInFilterAssets && (
            <FilterSearch
              value={searchValue}
              onChange={handleSearch}
              onClear={handleClear}
              searchPlaceholder={t('common:filters.searchInputPlaceholder')}
            />
          )}
          {isParentOptionActive && resetFilterOptions && (
            <>
              <div className={filterStyles.separator} />
              <FilterResetButton handleReset={handleReset} />
            </>
          )}
          <AssetsOptionsList
            options={filteredOptions}
            isChecked={isChecked}
            onFilterSelect={onFilterSelect}
            filterKey={filter.key}
            multiselect={multiselect}
            isPending={isPending}
          />
          {noResultsFound && (
            <p className={filterStyles.noResults}>
              {t('common:filters.noResults')}
            </p>
          )}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
};
